import { formatReportAmountAlways } from '../reports/shared/reportUtils';

export type LinkPickerSelection = {
  recordId: number;
  linkNumber: string | null;
  narrative: string;
  docCode: string | null;
  postingDate: string;
  dueDate: string | null;
  debitLocal: number;
  creditLocal: number;
  currencyCode: string | null;
  /** Link-level open balance when available; otherwise line debit. */
  openBalance: number | null;
  /** Amount to pay now (may be partial). Defaults to openBalance when omitted. */
  payAmount?: number | null;
};

export type PickerRecord = {
  id: number;
  documentId: number;
  linkNumber: string | null;
  postingDate: string;
  docCode: string | null;
  narrative: string;
  dueDate: string | null;
  debitLocal: number;
  creditLocal: number;
  debitUSD: number;
  creditUSD: number;
  currencyCode: string | null;
  linkBalance: number | null;
};

export type OpenLinkRow = {
  linkNumber: string;
  balance: number;
  currencyCode: string | null;
  earliestDueDate: string | null;
};

export type UnlinkedLineRow = {
  id: number;
  documentId: number;
  postingDate: string;
  docCode: string | null;
  narrative: string;
  dueDate: string | null;
  debitLocal: number;
  creditLocal: number;
  debitUSD: number;
  creditUSD: number;
  currencyCode: string | null;
  linkNumber?: string | null;
  linkBalance?: number | null;
};

export type LinkPickerResponse = {
  account?: { id: number; code: string; combLevel: string; name: string };
  company?: { id: number; code: string; name: string };
  asOfDate?: string;
  records?: PickerRecord[];
  openLinks?: OpenLinkRow[];
  unlinkedLines?: UnlinkedLineRow[];
};

const round2 = (n: number) => Math.round(n * 100) / 100;

const openBalanceLabel = (balance: number) => {
  const amount = Math.abs(balance);
  if (balance < 0) return `${formatReportAmountAlways(amount)} CR open`;
  if (balance > 0) return `${formatReportAmountAlways(amount)} DR open`;
  return formatReportAmountAlways(0);
};

export const isPayableChargeRecord = (
  row: Pick<PickerRecord, 'debitLocal' | 'creditLocal' | 'debitUSD' | 'creditUSD'>,
) => {
  if (row.creditLocal > 0.005 && row.debitLocal <= 0.005) return false;
  return row.debitLocal > 0.005
    || (row.debitLocal <= 0.005 && row.creditLocal <= 0.005 && row.debitUSD > 0.005);
};

export const rowOpenBalance = (row: Pick<PickerRecord, 'linkBalance' | 'debitLocal'>): number => {
  if (row.linkBalance != null && row.linkBalance > 0.005) return round2(row.linkBalance);
  if (row.debitLocal > 0.005) return round2(row.debitLocal);
  return 0;
};

export const toLinkPickerSelection = (
  row: PickerRecord,
  payAmount?: number | null,
): LinkPickerSelection => {
  const openBalance = rowOpenBalance(row);
  return {
    recordId: row.id,
    linkNumber: row.linkNumber?.trim() || null,
    narrative: row.narrative,
    docCode: row.docCode,
    postingDate: row.postingDate,
    dueDate: row.dueDate,
    debitLocal: row.debitLocal,
    creditLocal: row.creditLocal,
    currencyCode: row.currencyCode,
    openBalance: openBalance > 0.005 ? openBalance : null,
    ...(payAmount != null && payAmount > 0.005 ? { payAmount: round2(payAmount) } : {}),
  };
};

export const normalizePickerRecords = (data?: LinkPickerResponse): PickerRecord[] => {
  if (!data) return [];

  let rows: PickerRecord[] = [];

  if (Array.isArray(data.records) && data.records.length > 0) {
    rows = data.records;
  } else {
    const legacyCount = (data.openLinks?.length ?? 0) + (data.unlinkedLines?.length ?? 0);
    if (Array.isArray(data.records) && data.records.length === 0 && legacyCount === 0) {
      return [];
    }

    for (const link of data.openLinks ?? []) {
      if (link.balance <= 0.005) continue;
      rows.push({
        id: -Number.parseInt(link.linkNumber, 10) || -rows.length - 1,
        documentId: 0,
        linkNumber: link.linkNumber,
        postingDate: link.earliestDueDate ?? '',
        docCode: null,
        narrative: `Open link balance — ${openBalanceLabel(link.balance)}`,
        dueDate: link.earliestDueDate,
        debitLocal: link.balance,
        creditLocal: 0,
        debitUSD: 0,
        creditUSD: 0,
        currencyCode: link.currencyCode,
        linkBalance: link.balance,
      });
    }

    for (const line of data.unlinkedLines ?? []) {
      rows.push({
        id: line.id,
        documentId: line.documentId,
        linkNumber: line.linkNumber ?? null,
        postingDate: line.postingDate,
        docCode: line.docCode,
        narrative: line.narrative,
        dueDate: line.dueDate,
        debitLocal: line.debitLocal,
        creditLocal: line.creditLocal,
        debitUSD: line.debitUSD,
        creditUSD: line.creditUSD,
        currencyCode: line.currencyCode,
        linkBalance: line.linkBalance ?? null,
      });
    }
  }

  return rows.filter(isPayableChargeRecord);
};

export type PickedOpenItem = {
  record: PickerRecord;
  payAmount: number;
};

/**
 * Pick invoice lines oldest-first (due date, then posting date) and assign Pay now
 * amounts until the target is covered — full lines, then a partial on the last.
 * Uses each line's debit local (not aggregate link balance).
 */
export function pickOldestRecordsUntilAmount(
  records: PickerRecord[],
  targetAmount: number,
): PickedOpenItem[] {
  const target = round2(targetAmount);
  if (target <= 0.005 || records.length === 0) return [];

  const sorted = [...records]
    .map((row) => {
      const open = row.debitLocal > 0.005 ? round2(row.debitLocal) : rowOpenBalance(row);
      return {
        record: row,
        open,
        sortDue: row.dueDate?.trim() || row.postingDate?.trim() || '9999-12-31',
        sortPosting: row.postingDate?.trim() || '9999-12-31',
      };
    })
    .filter((c) => c.open > 0.005)
    .sort((a, b) => {
      const dueCmp = a.sortDue.localeCompare(b.sortDue);
      if (dueCmp !== 0) return dueCmp;
      const postCmp = a.sortPosting.localeCompare(b.sortPosting);
      if (postCmp !== 0) return postCmp;
      const linkCmp = (a.record.linkNumber || '').localeCompare(b.record.linkNumber || '');
      if (linkCmp !== 0) return linkCmp;
      return a.record.id - b.record.id;
    });

  const picked: PickedOpenItem[] = [];
  let remaining = target;

  for (const candidate of sorted) {
    if (remaining <= 0.005) break;
    const pay = round2(Math.min(remaining, candidate.open));
    if (pay <= 0.005) break;
    picked.push({
      record: candidate.record,
      payAmount: pay,
    });
    remaining = round2(remaining - pay);
  }

  return picked;
}
